/**
 * Barba.js page transitions — navigation without a full page reload.
 *
 * - Every internal link is fetched via Barba and only <main> is swapped.
 * - Normal links play the WebGL "torn paper" cover/reveal
 *   (custom-page-transition.js, exposed as window.bonjPageTransition).
 * - Pagination links (a.pagination__item) use a quick fade instead.
 */
(function () {
  'use strict';

  if (typeof window.barba === 'undefined') return;
  // Never hijack navigation inside the theme editor
  if (window.Shopify && window.Shopify.designMode) return;

  var barba = window.barba;
  var PREVENT_PATHS = /^\/(cart|checkout|account|challenge|password|apps)(\/|$|\?)|^\/(\d+\/)?(checkouts|orders)\//;

  function wait(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  function fade(el, to, ms) {
    if (!el) return Promise.resolve();
    el.style.transition = 'opacity ' + ms + 'ms ease';
    el.style.opacity = to;
    return wait(ms);
  }

  function fromPagination(data) {
    return !!(data.trigger && data.trigger.closest && data.trigger.closest('a.pagination__item'));
  }

  // Keep <head> in sync (title is handled by Barba itself)
  function syncHead(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var selectors = [
      'meta[name="description"]',
      'link[rel="canonical"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]'
    ];
    selectors.forEach(function (sel) {
      document.head.querySelectorAll(sel).forEach(function (n) { n.remove(); });
      doc.head.querySelectorAll(sel).forEach(function (n) {
        document.head.appendChild(document.importNode(n, true));
      });
    });
    if (doc.body) document.body.className = doc.body.className;
  }

  // Scripts inside swapped markup do not run on their own — re-run them.
  // External scripts that are already on the page are skipped so custom
  // elements are not defined twice.
  function runScripts(container) {
    container.querySelectorAll('script').forEach(function (old) {
      if (old.type && !/javascript|module/i.test(old.type)) return;
      if (old.src && document.querySelector('script[src="' + old.getAttribute('src') + '"]')) return;
      var s = document.createElement('script');
      Array.prototype.forEach.call(old.attributes, function (a) { s.setAttribute(a.name, a.value); });
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }

  function reinit(container) {
    runScripts(container);
    container.querySelectorAll('.shopify-section').forEach(function (section) {
      var id = (section.id || '').replace(/^shopify-section-/, '');
      section.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: id }
      }));
    });
    document.dispatchEvent(new CustomEvent('page:loaded', { detail: { container: container } }));
    window.dispatchEvent(new Event('resize'));
  }

  // Elements some sections move to <body> would otherwise be left behind
  // (unstyled, after the footer) once their page is swapped out.
  function cleanupBodyLeftovers() {
    document.querySelectorAll('body > .bonj-skirt-overlay').forEach(function (n) { n.remove(); });
    document.documentElement.classList.remove('bonj-skirt-is-open');
    document.body.style.overflow = '';
  }

  // The home-page zipper lives outside <main>; show it only on the index
  function syncGateway(data) {
    var gate = document.getElementById('bonj-gateway-v4');
    if (!gate) return;
    var isHome = data.next.namespace === 'index';
    if (isHome) gate.removeAttribute('hidden'); else gate.setAttribute('hidden', '');
  }

  function afterSwap(data) {
    syncGateway(data);
    syncHead(data.next.html);
    reinit(data.next.container);
  }

  barba.init({
    // A link clicked mid-transition would otherwise fall through to a real
    // full-page reload; ignore it until the running transition finishes.
    preventRunning: true,
    prevent: function (args) {
      var a = args.el;
      if (!a || !a.pathname) return false;
      if (a.hasAttribute('data-no-barba') || a.hasAttribute('download')) return true;
      if (PREVENT_PATHS.test(a.pathname)) return true;
      return false;
    },
    // Shopify can answer with redirects/404s; let those do a real load
    requestError: function (trigger, action, url, response) {
      window.location.href = url;
      return false;
    },
    timeout: 10000,
    transitions: [
      {
        // Pagination: fade the content, keep the paper transition out of it
        name: 'pagination-fade',
        custom: function (data) { return fromPagination(data); },
        leave: function (data) {
          cleanupBodyLeftovers();
          return fade(data.current.container, 0, 200);
        },
        enter: function (data) {
          data.next.container.style.opacity = 0;
          var grid = data.next.container.querySelector('[id^="ProductGridContainer--"]') || data.next.container;
          window.scrollTo({ top: Math.max(0, grid.getBoundingClientRect().top + window.scrollY - 100) });
          afterSwap(data);
          return wait(30).then(function () { return fade(data.next.container, 1, 250); }).then(function () {
            data.next.container.style.transition = '';
            data.next.container.style.opacity = '';
          });
        }
      },
      {
        // Everything else: WebGL torn-paper cover, swap, reveal
        name: 'paper',
        leave: function () {
          cleanupBodyLeftovers();
          var t = window.bonjPageTransition;
          return t && t.cover ? t.cover() : Promise.resolve();
        },
        enter: function (data) {
          window.scrollTo(0, 0);
          // Re-initialise the new content while the paper still covers the
          // screen, then give it time to settle so the refresh is never seen.
          // Scroll-reveal transitions are switched off while covered, so
          // items already in view finish their fade-in behind the paper.
          document.documentElement.classList.add('bonj-reveal-settle');
          afterSwap(data);
          var t = window.bonjPageTransition;
          return wait(600).then(function () {
            return new Promise(function (r) {
              requestAnimationFrame(function () { requestAnimationFrame(r); });
            });
          }).then(function () {
            window.scrollTo(0, 0);
            document.documentElement.classList.remove('bonj-reveal-settle');
            return t && t.reveal ? t.reveal() : undefined;
          });
        }
      }
    ]
  });

  // Barba appends the incoming <main> at the end of the wrapper (after the
  // footer). Put it right before the old one so `header + main` CSS keeps
  // matching, the layout order never changes, and "first main" is the new page.
  barba.hooks.beforeEnter(function (data) {
    var cur = data.current && data.current.container;
    var next = data.next && data.next.container;
    if (cur && next && cur !== next && cur.parentNode && cur.parentNode === next.parentNode) {
      cur.parentNode.insertBefore(next, cur);
    }
  });
})();
